/** Client for the community market API (server/routes/market.ts). */
import type {
  DeliveryMode,
  DeliveryOptions,
  ListingCard,
  ListingDetail,
  ProductOffers,
  MarketFilters,
  MarketNav,
  MarketSet,
  PaidPlan,
  SubscriptionView,
  MarketOrderStatus,
  MarketOrderView,
  MarketPaymentMethod,
  OfferView,
  ReviewView,
  SellerPublic,
  SellerStatus,
  TransferInfo,
  WalletInfo,
} from "../../shared/market";
import { marketFiltersToQuery, OFFER_PARAM, type MarketInbox, type MarketViewer, type PriceReference, type SellerMatch, type ShopAnnouncementInput, type ShopContent, type ShopBannerLink, type ShopSection, type ShopSectionView, type ReservationPage, type SellerReservation } from "../../shared/market";
import type { ImportItem, ImportPreview, ImportResult } from "../../shared/marketImport";
import type { ListingSignal, PanelSearchHit, SellerHome, SellerTask, Showcases, SellerInsights, SellerPerformance, SellerStatsView, StatsPeriod } from "../../shared/marketStats";
import type { CouponCheck, CouponStatus, SellerCoupon } from "../../shared/marketCoupons";
import type { CustomerInterests, CustomerOpportunities, CustomersView, MyCoupon } from "../../shared/marketCustomers";
import type { Automation, ShopEmailsView } from "../../shared/marketEmails";
import { wantedFiltersToQuery, type MyWant, type WantedBoard, type WantedFilters, type WantedMatches, type WantedProduct, type WantedProfile } from "../../shared/wanted";

export interface QuickReply {
  id: number;
  title: string;
  body: string;
}

export type AutomationView = Automation & { sent: number; clicks: number; orders: number };
import type { RatedZone } from "../../shared/shipping";
import { jsonInit, request } from "../lib/api";
import type { ApiKeyView, ApiKeysView, ApiRequestView, ApiScope, WebhookDeliveryView, WebhookView } from "../../shared/apiV1";

export interface WebhooksView {
  webhooks: WebhookView[];
  /** How many the plan allows (0: no API). */
  max: number;
}
import { resizeImage } from "../lib/resizeImage";
import { GAME_PARAM } from "../../shared/games";

export interface MySeller {
  handle: string;
  displayName: string;
  bio: string;
  contactPhone: string;
  socialUrl?: string | null;
  city: string;
  province: string;
  status: SellerStatus;
  statusReason: string | null;
  vacationUntil: string | null;
  transfer: TransferInfo | null;
  wallets: WalletInfo[];
  delivery: DeliveryOptions;
  acceptsOffers: boolean;
  acceptsCash: boolean;
  minOrderArs: number | null;
  mp: { connected: boolean; available: boolean; userId: string | null };
  payments: MarketPaymentMethod[];
  createdAt: string;
  /** New seller: big orders only with Mercado Pago or cash in hand. */
  isNew?: boolean;
  /** Approved with limits: buyers only get cash and the in-person handover (payments is already only cash). */
  limited?: boolean;
  kind?: "person" | "company";
  /** A company's legal name and CUIT, as it applied (the shop can't change them from its settings). */
  company?: { legalName: string; cuit: string } | null;
  /** Set up by pokefans: hidden until the owner activates it (accepts the rules; a person gives a birth date if missing). */
  managed?: { active: boolean; needsBirthDate: boolean } | null;
  /** Closed its "Guía de inicio" (it comes back if something required comes undone). */
  onboardingDismissed?: boolean;
  /** Sent the verification photo (only the admin sees it). */
  hasVerificationPhoto?: boolean;
  /** The customized shop's content (editable while the plan includes it). */
  shop?: ShopContent;
  /** The announcement over the shop's page, as saved. */
  announcement?: ShopAnnouncementInput | null;
  /** The shop's own sections (VMAX), as saved. */
  sections?: ShopSection[];
  /** Where the banner leads, as saved. */
  bannerLink?: ShopBannerLink | null;
  verification?: { verified: boolean; requestedAt: string | null };
  plan?: {
    name: "basic" | "ex" | "vmax";
    until: string | null;
    source: "founder" | "manual" | "mp_subscription" | null;
    /** null: no limit. */
    maxListings: number | null;
    maxPriceArs: number | null;
    liveListings: number;
    featuredPerMonth: number;
    featuredLeft: number;
    requestedAt: string | null;
    requested: "ex" | "vmax" | null;
  };
  reports?: { weekly: boolean; priceAlerts: boolean };
  /** Their Mercado Pago subscription to a paid plan (still going or being set up). */
  subscription?: SubscriptionView | null;
}

export interface MyListing extends ListingCard {
  status: "active" | "paused";
  notes: string;
  photos: string[];
  openOffers: number;
  favorites: number;
  /** Featured until then (a plan perk). */
  featuredUntil?: string | null;
  /** The site's dólar when its price was set ("Ajustar al dólar"); null: unknown. */
  priceRate?: number | null;
  /** "Precio en dólares": US$ cents; its pesos follow the dólar every day. */
  priceUsdCents?: number | null;
  /** Why today's pesos aren't the dollar price: held at the plan's top price. */
  usdHold?: "cap" | null;
}

export interface OrderSummaryRow {
  number: string;
  status: MarketOrderStatus;
  totalArs: number;
  paymentMethod: MarketPaymentMethod;
  deliveryMode: DeliveryMode;
  createdAt: string;
  other: string;
  title: string;
  image: string | null;
  unread: boolean;
  buyerMarkedPaid: boolean;
}

export interface CatalogCard {
  id: number;
  name: string;
  setName: string | null;
  cardNumber: string | null;
  rarity: string | null;
  imageKey: string;
  /** "singles", or a sealed product's category. */
  category: string;
  /** The product's print language ("en" for most of the catalog). */
  language?: string;
  /** Its game (the finishes a listing of it can have, shared/games.ts). */
  game?: string;
}

/** One of my own "Busco" (with its "Se busca" details). */
export type Want = MyWant;

/** What the "Se busca" form and Mi mercado › Busco can change (only the keys sent change). */
export interface WantFields {
  maxPriceArs?: number | null;
  conditionMin?: string | null;
  language?: string | null;
  quantity?: number;
  trade?: boolean;
  showPrice?: boolean;
  note?: string;
  province?: string | null;
  public?: boolean;
}

export interface NewOrderInput {
  lines: { listingId: number; quantity: number }[];
  offerId: number | null;
  paymentMethod: MarketPaymentMethod;
  deliveryMode: DeliveryMode;
  zone: RatedZone | null;
  meetingDate: string | null;
  buyer: { name: string; phone: string; address: string; city: string; notes: string };
  /** A shop coupon's code (the order checks it again). */
  couponCode?: string | null;
}

const same: RequestInit = { credentials: "same-origin" };
/** Longest side a shop banner and a shop logo are sent at. */
const BANNER_SIDE = 3200;
const LOGO_SIDE = 1024;
const enc = encodeURIComponent;

export const marketApi = {
  info: (signal?: AbortSignal) =>
    request<{ mpEnabled: boolean; usdArsRate: number | null; usdArsRateAt?: string | null; foundersLeft: number; planSubscriptions?: boolean; planPrices?: Record<PaidPlan, number> | null }>("/api/market/info", { signal }),
  /** The header's "Comprar" panel, of the visitor's game (GameProvider). */
  nav: (game: string, signal?: AbortSignal) => request<MarketNav>(`/api/market/nav?${GAME_PARAM}=${enc(game)}`, { signal }),
  /** The home's "Más colecciones": sets of `game` with something on sale, newest first. */
  sets: (game: string, signal?: AbortSignal) => request<MarketSet[]>(`/api/market/sets?${GAME_PARAM}=${enc(game)}`, { signal }),
  listings: (f: Partial<MarketFilters>, signal?: AbortSignal) =>
    request<{ items: ListingCard[]; grouped?: boolean; featured?: ListingCard[]; total: number; page: number; pageSize: number }>(`/api/market/listings?${marketFiltersToQuery(f)}`, { signal }),
  /** `src`: where the visit came from, for the seller's stats (only counted visits use it). */
  listing: (id: number, signal?: AbortSignal, src?: string) => request<ListingDetail>(`/api/market/listings/${id}${src ? `?src=${src}` : ""}`, { ...same, signal }),
  searchSellers: (q: string, signal?: AbortSignal) => request<SellerMatch[]>(`/api/market/sellers?q=${enc(q)}`, { signal }),
  /** A shop; its pins and sections are of `game` (the visitor's), else of the first open game. */
  seller: (handle: string, signal?: AbortSignal, game?: string) =>
    request<SellerPublic>(`/api/market/sellers/${enc(handle)}${game ? `?${GAME_PARAM}=${enc(game)}` : ""}`, { ...same, signal }),
  sellerReviews: (handle: string, page: number, signal?: AbortSignal) =>
    request<{ items: ReviewView[]; hasMore: boolean }>(`/api/market/sellers/${enc(handle)}/reviews?page=${page}`, { signal }),
  /** A card's offers; `offer`: the one a link points at, in the table even past the cap. */
  productSummary: (productId: number, signal?: AbortSignal, offer?: number | null) =>
    request<ProductOffers>(`/api/market/products/${productId}${offer ? `?${OFFER_PARAM}=${offer}` : ""}`, { signal }),
  /** The card's page was opened: a visit for each offer on it (the shop's stats). */
  productSeen: (productId: number, src?: string, offer?: number | null) =>
    request<{ counted: number }>(`/api/market/products/${productId}/seen`, { ...same, ...jsonInit("POST", { src, ...(offer ? { offer } : {}) }) }),
  /** The card a listing is of (its old /mercado/<id> address goes there). */
  listingProduct: (id: number, signal?: AbortSignal) => request<{ productSlug: string }>(`/api/market/listings/${id}/product`, { signal }),
  /** Catalog search for picking a card: 8 suggestions, or a page of 24 for the results grid; narrowed to a category and/or set. */
  cards: (q: string, opts: { page?: number; size?: 8 | 24; category?: string; set?: string; game?: string } = {}, signal?: AbortSignal) =>
    request<{ items: CatalogCard[]; total: number; fuzzy: boolean }>(
      `/api/market/cards?q=${enc(q)}&size=${opts.size ?? 8}&page=${opts.page ?? 1}${opts.category ? `&category=${enc(opts.category)}` : ""}${opts.set ? `&set=${enc(opts.set)}` : ""}${opts.game ? `&${GAME_PARAM}=${enc(opts.game)}` : ""}`,
      { signal },
    ),

  inbox: (signal?: AbortSignal) => request<MarketInbox>("/api/me/inbox", { ...same, signal }),
  mySeller: (signal?: AbortSignal) => request<MySeller | null>("/api/me/seller", { ...same, signal }),
  apply: (b: Record<string, unknown>) => request<MySeller>("/api/me/seller/apply", jsonInit("POST", b)),
  activate: (b: { acceptTerms: boolean; birthDate?: string }) => request<MySeller>("/api/me/seller/activate", jsonInit("POST", b)),
  saveSeller: (b: Record<string, unknown>) => request<MySeller>("/api/me/seller", jsonInit("PUT", b)),
  mpConnect: () => request<{ url: string }>("/api/me/seller/mp/connect", same),
  mpDisconnect: () => request<MySeller>("/api/me/seller/mp", { ...same, method: "DELETE" }),
  myListings: (signal?: AbortSignal) => request<MyListing[]>("/api/me/listings", { ...same, signal }),
  createListing: (b: Record<string, unknown>) => request<{ id: number }>("/api/me/listings", jsonInit("POST", b)),
  /** The card in a photo (the same reader as /identificar): catalog matches, best first. */
  identifyCard: async (photo: Blob) => {
    const form = new FormData();
    form.append("image", await resizeImage(photo), "card.jpg");
    return request<{ results: { id: number; name: { es: string; en: string }; setName: string | null; cardNumber: string | null; rarity: string | null; category: string; game?: string; imageKey: string }[] }>("/api/cards/identify", { ...same, method: "POST", body: form });
  },
  priceRef: (productId: number, condition: string, signal?: AbortSignal) => request<PriceReference | null>(`/api/me/price-ref/${productId}?condition=${enc(condition)}`, { ...same, signal }),
  updateListing: (id: number, b: Record<string, unknown>) => request<MyListing | null>(`/api/me/listings/${id}`, jsonInit("PATCH", b)),
  importPreview: (text: string) => request<ImportPreview>("/api/me/listings/import/preview", jsonInit("POST", { text })),
  importListings: (items: ImportItem[]) => request<ImportResult>("/api/me/listings/import", jsonInit("POST", { items })),
  updatePrices: (changes: { id: number; priceArs: number }[]) => request<{ updated: number; skipped: { id: number; code: string }[] }>("/api/me/listings/prices", jsonInit("POST", { changes })),
  stats: (days: StatsPeriod, signal?: AbortSignal) => request<SellerStatsView>(`/api/me/seller/stats?days=${days}`, { ...same, signal }),
  customers: (q: { segment: string; sort: string; page: number }, signal?: AbortSignal) =>
    request<CustomersView>(`/api/me/seller/customers?segment=${enc(q.segment)}&sort=${q.sort}&page=${q.page}`, { ...same, signal }),
  customerInterests: (signal?: AbortSignal) => request<CustomerInterests>("/api/me/seller/customers/interests", { ...same, signal }),
  customerOpportunities: (signal?: AbortSignal) => request<CustomerOpportunities>("/api/me/seller/customers/opportunities", { ...same, signal }),
  segmentCount: (segment: string, signal?: AbortSignal) => request<{ count: number | null }>(`/api/me/seller/segments/count?segment=${enc(segment)}`, { ...same, signal }),
  automations: (signal?: AbortSignal) => request<AutomationView[]>("/api/me/seller/automations", { ...same, signal }),
  saveAutomation: (kind: string, enabled: boolean, config: Record<string, unknown>) => request<AutomationView[]>(`/api/me/seller/automations/${kind}`, jsonInit("PUT", { enabled, config })),
  shopEmails: (signal?: AbortSignal) => request<ShopEmailsView>("/api/me/seller/emails", { ...same, signal }),
  sendShopEmail: (b: Record<string, unknown>) => request<ShopEmailsView>("/api/me/seller/emails", jsonInit("POST", b)),
  shopUnsubscribe: (u: string, s: string, t: string) => request<{ unsubscribed: true }>("/api/email/shop-unsubscribe", jsonInit("POST", { u, s, t })),
  shopSpam: (u: string, c: string, t: string) => request<{ reported: true }>("/api/email/shop-spam", jsonInit("POST", { u, c: Number(c), t })),
  myCoupons: (seller: string | null, signal?: AbortSignal) => request<MyCoupon[]>(`/api/me/coupons${seller ? `?seller=${enc(seller)}` : ""}`, { ...same, signal }),
  coupons: (signal?: AbortSignal) => request<SellerCoupon[]>("/api/me/seller/coupons", { ...same, signal }),
  createCoupon: (b: Record<string, unknown>) => request<SellerCoupon[]>("/api/me/seller/coupons", jsonInit("POST", b)),
  setCouponStatus: (id: number, status: CouponStatus) => request<SellerCoupon[]>(`/api/me/seller/coupons/${id}`, jsonInit("PATCH", { status })),
  checkCoupon: (b: { seller: string; code: string; lines: { listingId: number; quantity: number }[]; offerId: number | null }) => request<CouponCheck>("/api/market/coupons/check", jsonInit("POST", b)),
  insights: (days: StatsPeriod, page: number, signal?: AbortSignal) => request<SellerInsights>(`/api/me/seller/insights?days=${days}&page=${page}`, { ...same, signal }),
  quickReplies: (signal?: AbortSignal) => request<{ max: number; replies: QuickReply[] }>("/api/me/seller/quick-replies", { ...same, signal }),
  addQuickReply: (title: string, body: string) => request<{ max: number; replies: QuickReply[] }>("/api/me/seller/quick-replies", jsonInit("POST", { title, body })),
  deleteQuickReply: (id: number) => request<{ replies: QuickReply[] }>(`/api/me/seller/quick-replies/${id}`, { ...same, method: "DELETE" }),
  // Integraciones (public API, VMAX): the shop's API keys.
  apiKeys: (signal?: AbortSignal) => request<ApiKeysView>("/api/me/seller/api-keys", { ...same, signal }),
  createApiKey: (name: string, scopes: ApiScope[]) => request<ApiKeysView & { key: string; view: ApiKeyView }>("/api/me/seller/api-keys", jsonInit("POST", { name, scopes })),
  revokeApiKey: (id: number) => request<ApiKeysView>(`/api/me/seller/api-keys/${id}`, { ...same, method: "DELETE" }),
  apiRequests: (signal?: AbortSignal) => request<ApiRequestView[]>("/api/me/seller/api-requests", { ...same, signal }),
  webhooks: (signal?: AbortSignal) => request<WebhooksView>("/api/me/seller/webhooks", { ...same, signal }),
  createWebhook: (b: { url: string; types: string[]; description?: string }) => request<WebhooksView & { secret: string; webhook: WebhookView }>("/api/me/seller/webhooks", jsonInit("POST", b)),
  updateWebhook: (id: number, b: { url?: string; types?: string[]; description?: string; active?: boolean }) => request<WebhooksView>(`/api/me/seller/webhooks/${id}`, jsonInit("PATCH", b)),
  deleteWebhook: (id: number) => request<WebhooksView>(`/api/me/seller/webhooks/${id}`, { ...same, method: "DELETE" }),
  testWebhook: (id: number) => request<{ ok: boolean; code: number | null; ms: number | null; error: string | null }>(`/api/me/seller/webhooks/${id}/test`, jsonInit("POST", {})),
  rotateWebhookSecret: (id: number) => request<{ secret: string; previousValidUntil: string }>(`/api/me/seller/webhooks/${id}/rotate-secret`, jsonInit("POST", {})),
  webhookDeliveries: (id: number, signal?: AbortSignal) => request<{ items: WebhookDeliveryView[]; next_cursor: string | null }>(`/api/me/seller/webhooks/${id}/deliveries`, { ...same, signal }),
  retryWebhookDelivery: (id: number, deliveryId: number) => request<{ retried: boolean }>(`/api/me/seller/webhooks/${id}/deliveries/${deliveryId}/retry`, jsonInit("POST", {})),
  setReports: (b: { weekly: boolean; priceAlerts: boolean }) => request<MySeller>("/api/me/seller/reports", jsonInit("PUT", b)),
  accountingCsvUrl: () => "/api/me/seller/accounting.csv",
  performance: (days: StatsPeriod, signal?: AbortSignal) => request<SellerPerformance>(`/api/me/seller/performance?days=${days}`, { ...same, signal }),
  home: (signal?: AbortSignal) => request<SellerHome>("/api/me/seller/home", { ...same, signal }),
  tasks: (signal?: AbortSignal) => request<SellerTask[]>("/api/me/seller/tasks", { ...same, signal }),
  /** Takes a task off "Para hacer hoy" (what it had as shown), and brings every dismissed one back. */
  dismissTask: (task: Pick<SellerTask, "kind" | "keys">) => request<{ ok: true }>("/api/me/seller/tasks/dismiss", jsonInit("POST", { kind: task.kind, keys: task.keys })),
  restoreTasks: () => request<{ ok: true }>("/api/me/seller/tasks/restore", jsonInit("POST", {})),
  dismissOnboarding: () => request<{ ok: true }>("/api/me/seller/onboarding/dismiss", jsonInit("POST", {})),
  signals: (signal?: AbortSignal) => request<ListingSignal[]>("/api/me/seller/signals", { ...same, signal }),
  panelSearch: (q: string, signal?: AbortSignal) => request<PanelSearchHit[]>(`/api/me/seller/search?q=${encodeURIComponent(q)}`, { ...same, signal }),
  showcase: (signal?: AbortSignal) => request<Showcases>("/api/me/seller/showcase", { ...same, signal }),
  /** "Destacadas en mi tienda": the pinned listing ids, in order. */
  shopPins: (signal?: AbortSignal) => request<{ ids: number[]; cards: ListingCard[] }>("/api/me/seller/pins", { ...same, signal }),
  reservations: (signal?: AbortSignal) => request<SellerReservation[]>("/api/me/seller/reservations", { ...same, signal }),
  createReservation: (b: { listingId: number; quantity: number; priceArs: number; note: string }) => request<{ id: number; token: string }>("/api/me/seller/reservations", jsonInit("POST", b)),
  cancelReservation: (id: number) => request<SellerReservation[]>(`/api/me/seller/reservations/${id}/cancel`, jsonInit("POST", {})),
  reservationPage: (token: string, signal?: AbortSignal) => request<ReservationPage>(`/api/market/reservations/${enc(token)}`, { ...same, signal }),
  claimReservation: (token: string) => request<{ offerId: number; handle: string; listingId: number; quantity: number }>(`/api/market/reservations/${enc(token)}/claim`, jsonInit("POST", {})),
  /** What each section would show now, without saving. */
  previewSections: (sections: ShopSection[], signal?: AbortSignal) => request<ShopSectionView[]>("/api/me/seller/sections/preview", { ...jsonInit("POST", { sections }), signal }),
  pinOptions: (q: string, signal?: AbortSignal) => request<ListingCard[]>(`/api/me/seller/pins/options?q=${enc(q)}`, { ...same, signal }),
  saveShopPins: (listingIds: number[]) => request<number[]>("/api/me/seller/pins", jsonInit("PUT", { listingIds })),
  /** A plain link (the browser downloads it with the session cookie). */
  ordersCsvUrl: (from: string, to: string) => `/api/me/seller/orders.csv?from=${enc(from)}&to=${enc(to)}`,
  removeListing: (id: number) => request<{ removed: true }>(`/api/me/listings/${id}`, { ...same, method: "DELETE" }),
  requestVerification: () => request<MySeller>("/api/me/seller/verification", jsonInit("POST", {})),
  demand: (game: string, signal?: AbortSignal) =>
    request<{ productId: number; productSlug: string; name: string; setName: string | null; cardNumber: string | null; image: string; wanting: number; lastAt: string; mine: boolean; marketCount: number; marketMinArs: number | null; onBoard: number }[]>(
      `/api/me/seller/demand?${GAME_PARAM}=${enc(game)}`,
      { ...same, signal },
    ),
  featureListing: (id: number) => request<{ featuredUntil: string; left: number }>(`/api/me/listings/${id}/feature`, jsonInit("POST", {})),
  /** The customized shop's banner (4:1) or logo (square). */
  uploadShopImage: async (kind: "banner" | "logo", file: Blob) => {
    const form = new FormData();
    // A banner is cropped to 1600 × 400 from the middle: more width, so a tall photo isn't enlarged. A logo keeps its transparency.
    form.append("image", await resizeImage(file, kind === "banner" ? { maxSide: BANNER_SIDE } : { maxSide: LOGO_SIDE, alpha: true }), kind);
    return request<MySeller>(`/api/me/seller/${kind}`, { ...same, method: "POST", body: form });
  },
  deleteShopImage: (kind: "banner" | "logo") => request<MySeller>(`/api/me/seller/${kind}`, { ...same, method: "DELETE" }),
  requestPlan: (plan: "ex" | "vmax") => request<MySeller>("/api/me/seller/plan", jsonInit("POST", { plan })),
  /** "Suscribirme": the Mercado Pago link to add a card (the plan turns on with the first charge). */
  subscribe: (plan: PaidPlan, payerEmail: string) => request<{ url: string }>("/api/me/seller/subscription", jsonInit("POST", { plan, payerEmail })),
  cancelSubscription: () => request<MySeller>("/api/me/seller/subscription", { ...same, method: "DELETE" }),
  uploadVerification: async (file: Blob) => {
    const form = new FormData();
    form.append("image", await resizeImage(file), "verification");
    return request<MySeller>("/api/me/seller/verification-photo", { ...same, method: "POST", body: form });
  },
  uploadPhoto: async (id: number, file: Blob) => {
    const form = new FormData();
    form.append("image", await resizeImage(file), "photo");
    return request<{ photos: string[] }>(`/api/me/listings/${id}/photos`, { ...same, method: "POST", body: form });
  },
  editPhoto: (id: number, index: number, action: "delete" | "cover") => request<{ photos: string[] }>(`/api/me/listings/${id}/photos/${index}`, jsonInit("PATCH", { action })),

  orders: (role: "buyer" | "seller", signal?: AbortSignal) => request<OrderSummaryRow[]>(`/api/market/orders?role=${role}`, { ...same, signal }),
  createOrder: (input: NewOrderInput) => request<{ number: string; redirectUrl: string | null }>("/api/market/orders", jsonInit("POST", input)),
  order: (number: string, paymentId?: string | null, signal?: AbortSignal) =>
    request<MarketOrderView>(`/api/market/orders/${enc(number)}${paymentId ? `?payment_id=${enc(paymentId)}` : ""}`, { ...same, signal }),
  pay: (number: string) => request<{ redirectUrl: string }>(`/api/market/orders/${enc(number)}/pay`, jsonInit("POST", {})),
  step: (number: string, step: string, b: Record<string, unknown> = {}) => request<MarketOrderView>(`/api/market/orders/${enc(number)}/actions/${step}`, jsonInit("POST", b)),
  paymentTx: (number: string, tx: string) => request<MarketOrderView>(`/api/market/orders/${enc(number)}/payment-tx`, jsonInit("POST", { tx })),
  message: (number: string, body: string) => request<MarketOrderView>(`/api/market/orders/${enc(number)}/messages`, jsonInit("POST", { body })),
  review: (number: string, stars: number, body: string) => request<MarketOrderView>(`/api/market/orders/${enc(number)}/review`, jsonInit("POST", { stars, body })),
  reply: (number: string, body: string) => request<MarketOrderView>(`/api/market/orders/${enc(number)}/reply`, jsonInit("POST", { body })),

  offers: (signal?: AbortSignal) => request<OfferView[]>("/api/me/offers", { ...same, signal }),
  makeOffer: (listingId: number, quantity: number, priceArs: number) => request<{ id: number }>(`/api/market/listings/${listingId}/offers`, jsonInit("POST", { quantity, priceArs })),
  respondOffer: (id: number, action: "accept" | "reject" | "counter" | "cancel", counterArs?: number) =>
    request<OfferView[]>(`/api/me/offers/${id}`, jsonInit("POST", { action, counterArs })),

  wants: (signal?: AbortSignal) => request<Want[]>("/api/me/wants", { ...same, signal }),
  /** Without maxPriceArs, a "Busco" already saved keeps its price. */
  addWant: (productId: number, maxPriceArs?: number | null) => request<Want[]>("/api/me/wants", jsonInit("POST", { productId, maxPriceArs })),
  deleteWant: (productId: number) => request<{ deleted: true }>(`/api/me/wants/${productId}`, { ...same, method: "DELETE" }),
  /** Saves a "Busco" with its "Se busca" details (public: on the board). */
  saveWant: (productId: number, fields: WantFields) => request<Want[]>("/api/me/wants", jsonInit("POST", { productId, ...fields })),
  updateWant: (productId: number, change: WantFields & { bump?: boolean; fulfilled?: boolean }) => request<Want[]>(`/api/me/wants/${productId}`, jsonInit("PATCH", change)),
  wantedBoard: (f: Partial<WantedFilters>, signal?: AbortSignal) => {
    const qs = wantedFiltersToQuery(f);
    return request<WantedBoard>(`/api/market/wanted${qs ? `?${qs}` : ""}`, { ...same, signal });
  },
  /** fresh: skips the browser's cached copy (right after publishing, to see it). */
  wantedCard: (slug: string, signal?: AbortSignal, fresh = false) => request<WantedProduct>(`/api/market/wanted/${enc(slug)}`, { ...same, signal, ...(fresh ? { cache: "no-store" as const } : {}) }),
  wantedMatches: (signal?: AbortSignal) => request<WantedMatches>("/api/me/wanted/matches", { ...same, signal }),
  wantedProfile: (signal?: AbortSignal) => request<WantedProfile>("/api/me/wanted/profile", { ...same, signal }),
  setNickname: (nickname: string) => request<WantedProfile>("/api/me/wanted/profile", jsonInit("PUT", { nickname })),
  viewer: (signal?: AbortSignal) => request<MarketViewer>("/api/me/market/viewer", { ...same, signal }),
  favorites: (signal?: AbortSignal) => request<ListingDetail[]>("/api/me/favorites", { ...same, signal }),
  setFavorite: (id: number, on: boolean) => request<{ favorite: boolean }>(`/api/me/favorites/${id}`, { ...same, method: on ? "PUT" : "DELETE" }),
  following: (signal?: AbortSignal) => request<{ handle: string; displayName: string; avatarUrl: string | null; listings: number }[]>("/api/me/following", { ...same, signal }),
  follow: (handle: string, on: boolean) => request<{ following: boolean }>(`/api/market/sellers/${enc(handle)}/follow`, { ...same, method: on ? "PUT" : "DELETE" }),
  report: (targetType: string, targetId: string, reason: string, details: string) => request<{ id: number }>("/api/market/reports", jsonInit("POST", { targetType, targetId, reason, details })),
};
